import { FC, useState } from 'react';

import {
    GridPaginationModel,
    GridSortModel,
    Loading,
} from '@thirdspacelearning/library';

import LearningObjectivesFilters from './LearningObjectivesFilters';
import { LearningObjectivesTable } from './learning-objectives-table';

import { useLoSelection } from '@hooks';
import {
    Country,
    LearningObjective,
    LearningObjectiveType,
    SearchParams,
    Year,
} from '@types';
import { defaultSearchParams, defaultTableParams } from '@utils';
import { ErrorActionPanel } from '@shared';

interface Props {
    onEditObjective: (learningObjective: LearningObjective) => void;
    country: Country;
}

const LearningObjectivesContent: FC<Props> = ({ onEditObjective, country }) => {
    const [searchParams, setSearchParams] = useState<SearchParams>({
        ...defaultSearchParams,
        objective_type: LearningObjectiveType.STANDARD,
    });

    const [paginationModel, setPaginationModel] = useState<GridPaginationModel>(
        {
            page: defaultTableParams.page,
            pageSize: defaultTableParams.per_page,
        },
    );

    const { los, years, isInitialLoad, isError, isGridLoading } =
        useLoSelection(searchParams, paginationModel, country);

    const handleSort = (sortModel: GridSortModel) => {
        let sort = '';

        if (sortModel.length > 0) {
            const sorting = sortModel[0].sort === 'asc' ? '' : '-';
            sort = `${sorting}${sortModel[0].field}`;
        }

        setSearchParams({
            ...searchParams,
            sort,
        });
    };

    return (
        <>
            {isInitialLoad && (
                <Loading loadingMessage="Loading learning objectives..." />
            )}

            {isError && (
                <ErrorActionPanel
                    error="There was an error loading the learning objectives"
                    message="Please refresh the page and try again. If the problem persists, please let us know."
                />
            )}

            {los && years && (
                <>
                    <LearningObjectivesFilters
                        years={years as Year[]}
                        searchParams={searchParams}
                        setSearchParams={setSearchParams}
                        paginationModel={paginationModel}
                        setPaginationModel={setPaginationModel}
                        country={country}
                    />
                    <LearningObjectivesTable
                        learningObjectives={
                            los.learningObjectives as LearningObjective[]
                        }
                        total={los.totalCount as number}
                        loading={isGridLoading}
                        paginationModel={paginationModel}
                        onPaginationModelChange={setPaginationModel}
                        onSortModelChange={handleSort}
                        onEditObjective={onEditObjective}
                        country={country}
                    />
                </>
            )}
        </>
    );
};

export default LearningObjectivesContent;
